import React from 'react';
import { IconButton, Menu } from '@parsina/framework';
import { MoreHorizontal } from 'lucide-react';

import { C } from '../../constants/theme';
import { useTranslation } from '../../i18n/I18nProvider.jsx';
import { patientName } from '../../lib/caseForm.js';
import { isArchived } from './Patients.shared.js';

// The ⋯ menu a patient row carries — «بایگانی» / «خروج از بایگانی» and «حذف».
//
// A component per ROW rather than one menu the list repositions: the kit's
// `Menu` measures itself against an anchor ref, and one shared anchor would open
// every row's menu beside the first row's button.
//
// Both surfaces render this one. The table puts it at the end of the action
// column, the card puts it in the card's own action row; the two destructive
// actions must not differ between them.

const MENU_ITEM = {
  display: 'block',
  width: '100%',
  textAlign: 'start',
  background: 'none',
  border: 0,
  padding: '11px 12px',
  fontSize: 13,
  cursor: 'pointer',
  color: C.textPrimary,
  font: 'inherit',
};

export default function PatientRowActions({
  row, onOpen, onArchive, onDelete, size = 34,
}) {
  const { t } = useTranslation();
  const anchorRef = React.useRef(null);
  const [open, setOpen] = React.useState(false);

  return (
    <>
      {/* The kit's IconButton does not forward a ref, and `Menu` measures itself
          against one — so the anchor is the span around it. */}
      <span ref={anchorRef} style={{ display: 'inline-flex' }}>
        <IconButton
          variant="ghost"
          size={size}
          icon={<MoreHorizontal size={16} aria-hidden />}
          label={t('patients.moreLabel', { title: patientName(row, t) })}
          onClick={(e) => { e.stopPropagation(); setOpen((v) => !v); }}
          data-testid={`patient-more-${row.id}`}
        />
      </span>
      <Menu
        open={open}
        onClose={() => setOpen(false)}
        anchorRef={anchorRef}
        ariaLabel={t('common.actions.more')}
        width={220}
        placement="auto"
        data-testid="patient-row-menu"
      >
        {/* «باز کردن پرونده» first (core.md §7.3): the row itself opens the
            record, but a menu is where a keyboard or screen-reader user
            finds the actions, and the primary one must be listed there too. */}
        {onOpen && (
          <button
            type="button"
            role="menuitem"
            style={MENU_ITEM}
            onClick={() => { setOpen(false); onOpen(row); }}
            data-testid={`patient-open-${row.id}`}
          >
            {t('patients.actions.open')}
          </button>
        )}
        <button
          type="button"
          role="menuitem"
          style={MENU_ITEM}
          onClick={() => { setOpen(false); onArchive(row); }}
          data-testid={`patient-archive-${row.id}`}
        >
          {t(isArchived(row) ? 'patients.actions.unarchive' : 'patients.actions.archive')}
        </button>
        <button
          type="button"
          role="menuitem"
          style={{ ...MENU_ITEM, color: C.red }}
          onClick={() => { setOpen(false); onDelete(row); }}
          data-testid={`patient-delete-${row.id}`}
        >
          {t('patients.actions.delete')}
        </button>
      </Menu>
    </>
  );
}
